import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';
import { CartAddEvent, CartErrorEvent } from '@theme/events';
import * as G from '@theme/hex-geometry';

/**
 * <hex-configurator>: build your own hexagon light.
 *
 * Reads #hex-data (rendered by snippets/hex-configurator-data.liquid), runs the
 * geometry engine (@theme/hex-geometry) and renders the SVG preview, the kit
 * review, the parts table and the price. No price, variant ID or business rule
 * lives in this file. Editor behaviour follows spec section 4.5.
 *
 * State: #design (G.createDesign) is the only record of the shape and its
 * undo/redo history. The hexagon count, the layout choices, the drawing, the
 * parts, the price, the share link and the printable plan are all derived from
 * it in #render, so they cannot disagree. Every control goes through one of
 * the G.* transitions (setCount, applyLayout, editCells, resetDesign,
 * undoDesign, redoDesign) and then #apply.
 *
 * Add to basket (spec 4.6): one /cart/add.js request with the parent line and
 * every part nested under it (parent_id). Falls back to flat lines if the
 * store rejects nesting. Any stock shortfall rolls the whole design back
 * (never leave a partial design in the basket). The guard is fail closed: no
 * data, a missing variant or short stock disables the button.
 *
 * Analytics (spec 4.8): every state change publishes `hex_configurator` to
 * Shopify.analytics and to window.dataLayer (GTM loads directly in
 * theme.liquid). The standard GA4 add_to_cart event is pushed by the fetch
 * interceptor in snippets/trl-datalayer.liquid for every /cart/add call, so
 * it is not pushed again here.
 */

const NS = 'http://www.w3.org/2000/svg';
const DRAG_PX = 6;
const TRACK_MS = 250;
/** Below this width the review sits under the design, so the sticky bar is offered. */
const BAR_MQ = '(max-width: 1199px)';
/** Smallest on-screen size for drawing labels, in CSS px. */
const LABEL_PX = 13;

/** Roles the design always needs, in table order. */
const LINE_ORDER = ['tube', 'V', 'Y', 'P', 'cable', 'bag'];
const LINE_LABELS = { tube: '410mm LED tube, 7W 6500K', V: 'V connector 120°', Y: 'Y connector 120° 3-way', P: 'Power connector 120°', cable: 'UK plug cable 1.5m', bag: 'Fixings bag', suspension: 'Suspension chain set' };

/** @param {string} tag @param {Record<string, string|number>} attrs */
function svgEl(tag, attrs = {}) {
  const e = document.createElementNS(NS, tag);
  for (const k in attrs) e.setAttribute(k, String(attrs[k]));
  return e;
}
const poly = (c) => c.map((p) => p[0].toFixed(1) + ',' + p[1].toFixed(1)).join(' ');
/** Sizes round up to the centimetre (G.upCm), so footprint plus clearance always adds up on screen. */
const m2 = (mm) => G.upCm(mm).toFixed(2);
const metres = (mm) => m2(mm) + ' m';
const plural = (n, word) => n + ' ' + word + (n === 1 ? '' : 's');

class HexConfigurator extends Component {
  requiredRefs = ['message', 'nRange', 'nNum', 'preset', 'options', 'layoutNote', 'canvas', 'stats', 'summary', 'bomBody', 'powerBox', 'price', 'addButton', 'stockNote', 'basketMsg', 'flash', 'shareFlash', 'editHint', 'undoBtn', 'redoBtn', 'removeBtn', 'resetBtn', 'enlargeBtn', 'editor', 'editorHome', 'dialog', 'dialogBody', 'dialogStats', 'review', 'stage', 'bar', 'barPrice'];

  /** @type {any} */ #data = null;
  /** @type {any} */ #rules = G.DEFAULT_RULES;
  /** @type {Record<string, any>} */ #parts = {};
  /** Shape, template and history: the single source of truth for the count. */
  #design = G.createDesign(new Set(['0,0']));
  /** @type {{colour:'white'|'black', suspension:boolean, selected:string|null}} */
  #state = { colour: 'white', suspension: false, selected: null };
  /** Outputs derived from #design on the last render. */
  #last = { d: null, g: null, power: null, bom: null, lines: [], total: 0, ok: false };
  #timer = 0;
  #ready = false;
  /** Last room recommendation (G.recommendCount), keyed by the text in the two room fields. */
  #rec = { key: '', r: null };
  #busy = false;
  /** @type {string|null} */ #focusCell = null;
  /** @type {string|null} */ #focusOption = null;
  #seen = { app: false, review: false, typing: false };
  /** @type {IntersectionObserver|null} */ #io = null;
  /** @type {ResizeObserver|null} */ #ro = null;
  /** @type {MediaQueryList|null} */ #mq = null;
  /** @type {boolean|null} */ #partsWasOpen = null;
  /** False when the market's prices exclude tax (for example a B2B catalogue); the header VAT switch then has nothing to remove. */
  #taxesIncluded = true;

  connectedCallback() {
    super.connectedCallback();
    try {
      this.#init();
    } catch (err) {
      console.error('[hex-configurator]', err);
      this.#fail('The builder could not start. Please refresh the page or call us and we will design your kit for you.');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.#timer);
    this.#io?.disconnect();
    this.#ro?.disconnect();
    this.#mq?.removeEventListener('change', this.#updateBar);
    window.removeEventListener('beforeprint', this.#beforePrint);
    window.removeEventListener('afterprint', this.#afterPrint);
    window.removeEventListener('vatPreferenceChanged', this.#onVatChange);
    document.documentElement.style.scrollPaddingBottom = '';
  }

  // ---------- setup ----------

  #init() {
    const el = document.getElementById('hex-data');
    if (!el) throw new Error('#hex-data missing');
    this.#data = JSON.parse(el.textContent || '{}');
    this.#rules = this.#mergeRules(this.#data.rules);
    this.#parts = this.#indexParts(this.#data.parts_list || []);
    const missing = this.#missingRoles();
    if (missing.length) {
      this.#fail('The builder is temporarily unavailable (missing: ' + missing.join(', ') + '). Please call us and we will design your kit for you.');
      return;
    }
    this.#fillPresets();
    const shared = G.decodeDesign(new URLSearchParams(location.search), this.#rules);
    if (shared) {
      this.#state.colour = shared.colour;
      const id = G.layoutChoices(G.createDesign(shared.cells)).selected;
      this.#design = G.createDesign(shared.cells, id ? { id, n: shared.cells.size } : null);
    } else {
      this.#state.colour = this.dataset.defaultColour === 'black' ? 'black' : 'white';
      const n = Math.max(1, Math.min(this.#maxN(), Number(this.dataset.defaultN) || 8));
      const o = G.buildOptions(n)[0];
      this.#design = G.createDesign(o.cells, { id: o.id, n });
    }
    if (!this.#parts.suspension) this.refs.suspensionToggle?.closest('label')?.remove();
    this.#taxesIncluded = this.dataset.taxesIncluded !== 'false';
    this.#syncColour();
    this.#ready = true;
    this.#render('start');
    this.#watch();
  }

  /** Observers for the sticky bar, label sizing, the editor dialog and printing. */
  #watch() {
    this.refs.dialog.addEventListener('close', () => this.#onEditorClose());
    this.#ro = new ResizeObserver(() => this.#fitLabels());
    this.#ro.observe(this.refs.canvas);
    this.#mq = window.matchMedia(BAR_MQ);
    this.#mq.addEventListener('change', this.#updateBar);
    this.#io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.target === this.refs.review) this.#seen.review = e.isIntersecting;
        else this.#seen.app = e.isIntersecting;
      }
      this.#updateBar();
    }, { rootMargin: '0px 0px -96px 0px' });
    // Offered once the design itself is on screen, not while reading the intro.
    this.#io.observe(this.refs.stage);
    this.#io.observe(this.refs.review);
    // The phone keyboard pushes fixed elements up over the fields; hide the bar while typing.
    this.addEventListener('focusin', (e) => {
      if (/** @type {Element} */ (e.target).matches('input:not([type="checkbox"]):not([type="range"]), select')) { this.#seen.typing = true; this.#updateBar(); }
    });
    this.addEventListener('focusout', () => { if (this.#seen.typing) { this.#seen.typing = false; this.#updateBar(); } });
    window.addEventListener('beforeprint', this.#beforePrint);
    window.addEventListener('afterprint', this.#afterPrint);
    // The header EX VAT / INC VAT switch (sections/contact-bar.liquid).
    window.addEventListener('vatPreferenceChanged', this.#onVatChange);
  }

  #mergeRules(raw) {
    const r = { ...G.DEFAULT_RULES };
    if (!raw) return r;
    for (const k of Object.keys(r)) {
      const v = raw[k];
      if (v === null || v === undefined || v === '') continue;
      if (k === 'chart_feeds' || k === 'fixings') {
        const obj = typeof v === 'string' ? JSON.parse(v) : v;
        if (obj && typeof obj === 'object') r[k] = { ...r[k], ...obj };
      } else if (typeof r[k] === 'number') {
        const num = Number(v);
        if (Number.isFinite(num)) r[k] = num;
      }
    }
    return r;
  }

  /** Build { tube:{white,black}, V, Y, P, cable:{white,black}, bag, suspension } from the flat list. */
  #indexParts(list) {
    const parts = {};
    for (const p of list) {
      let code = null;
      if (p.role === 'tube') code = 'tube';
      else if (p.role === 'connector' && (p.joint === 'V' || p.joint === 'Y')) code = p.joint;
      else if (p.role === 'power') code = 'P';
      else if (p.role === 'cable') code = 'cable';
      else if (p.role === 'accessory') code = 'bag';
      else if (p.role === 'suspension') code = 'suspension';
      if (!code) continue;
      if (code === 'tube' || code === 'cable') {
        parts[code] = parts[code] || {};
        if (p.colour === 'any') { parts[code].white = parts[code].white || p; parts[code].black = parts[code].black || p; }
        else parts[code][p.colour] = p;
      } else if (!parts[code]) {
        parts[code] = p;
      }
    }
    return parts;
  }

  #missingRoles() {
    const out = [];
    for (const code of LINE_ORDER) {
      const p = this.#parts[code];
      if (!p) out.push(LINE_LABELS[code]);
      else if ((code === 'tube' || code === 'cable') && (!p.white || !p.black)) out.push(LINE_LABELS[code] + ' (both colours)');
    }
    const parent = this.#data.parent;
    if (!parent || !parent.white || !parent.black) out.push('parent product');
    return out;
  }

  #fail(text) {
    this.refs.message.textContent = text;
    this.classList.add('hxc__app--failed');
    this.refs.addButton.disabled = true;
    this.refs.canvas.replaceChildren();
    if (this.refs.bar) this.refs.bar.hidden = true;
  }

  #fillPresets() {
    const sel = this.refs.preset;
    const sizes = Object.keys(this.#data.kits || {}).map(Number).filter((n) => n > 0).sort((a, b) => a - b);
    for (const n of sizes) {
      const o = document.createElement('option');
      o.value = String(n);
      o.textContent = plural(n, 'hexagon');
      sel.appendChild(o);
    }
  }

  // ---------- part lookup ----------

  #part(code) {
    const p = this.#parts[code];
    if (!p) return null;
    return code === 'tube' || code === 'cable' ? p[this.#state.colour] || null : p;
  }

  #maxN() { return this.#rules.max_hexagons_online; }

  // ---------- state transitions ----------

  /**
   * Adopt a new design state and redraw everything from it. A transition that
   * changed nothing returns the same object, in which case only the count
   * controls are re-synced (for example after typing an out-of-range number).
   */
  #apply(next, action) {
    if (next === this.#design) { this.#syncCount(); return; }
    this.#design = next;
    if (this.#state.selected && !next.cells.has(this.#state.selected)) this.#state.selected = null;
    this.#render(action);
  }

  /** End a run of count changes so the next one is its own undo step. */
  #endCountRun() {
    if (this.#design.last === 'count') this.#design = { ...this.#design, last: '' };
  }

  #edit(k, add) {
    const next = new Set(this.#design.cells);
    if (add) next.add(k); else next.delete(k);
    const r = G.editCells(this.#design, next, add ? 'edit_add' : 'edit_remove', this.#rules);
    if (r.error) {
      this.#flash(r.error === 'max' ? 'Maximum ' + this.#maxN() + ' hexagons online. Larger designs by quote.' : r.error === 'min' ? 'Keep at least one hexagon.' : 'This hexagon holds the light together. Removing it would split the light in two.');
      return;
    }
    if (!add) this.#state.selected = null;
    this.#apply(r.state, add ? 'edit_add' : 'edit_remove');
  }

  #move(from, to) {
    const next = new Set(this.#design.cells);
    next.delete(from);
    next.add(to);
    const r = G.editCells(this.#design, next, 'edit_move', this.#rules);
    this.#state.selected = r.error ? this.#state.selected : to;
    this.#apply(r.state, 'edit_move');
  }

  #removable(k) {
    const cells = this.#design.cells;
    if (!cells.has(k) || cells.size < 2) return false;
    const rest = new Set(cells);
    rest.delete(k);
    return G.isConnected(rest);
  }

  /** First tap selects a hexagon; tapping the selected one removes it. */
  #tapCell(k) {
    if (this.#state.selected === k) { this.#edit(k, false); return; }
    this.#state.selected = k;
    this.#drawSvg();
    this.#updateEditControls();
  }

  // ---------- declarative handlers (on:*) ----------

  /** Slider (input and change) and number box (change). @param {Event} event */
  onCountInput(event) {
    const input = /** @type {HTMLInputElement} */ (event.target);
    const v = Number(input.value);
    if (input.value !== '' && Number.isFinite(v)) this.#apply(G.setCount(this.#design, v, this.#rules), 'count');
    else this.#syncCount();
    if (event.type === 'change') this.#endCountRun();
  }

  /** The − and + buttons beside the count; each tap is its own undo step. @param {number} delta */
  stepCount(delta) {
    const n = this.#design.cells.size + Number(delta);
    if (!(n >= 1 && n <= this.#maxN())) return;
    this.#endCountRun();
    this.#apply(G.setCount(this.#design, n, this.#rules), 'count');
    this.#endCountRun();
  }

  /**
   * A recommended number from the room check: that count in a layout that
   * fits the ceiling, keeping the chosen layout when it fits. A design of that
   * size that already fits (for example one the customer edited) is kept.
   * @param {number} n
   */
  useCount(n) {
    n = Number(n);
    const r = this.#rec.r, ids = r && r.fits && r.fits.get(n);
    const d = this.#last.d;
    if (!ids || !d) return;
    if (n === this.#design.cells.size && G.fitCheck(this.refs.roomW.value, this.refs.roomD.value, d.widthMm, d.depthMm).status === 'fits') return;
    const cur = G.layoutChoices(this.#design).selected;
    this.#endCountRun();
    this.#apply(G.setCount(this.#design, n, this.#rules, ids.includes(cur) ? cur : ids[0]), 'room_recommend');
    this.#endCountRun();
  }

  /** A standard kit size: that count, in the Compact layout the kits use. @param {Event} event */
  onPreset(event) {
    const v = Number(/** @type {HTMLSelectElement} */ (event.target).value);
    if (!(v > 0)) return;
    this.#endCountRun();
    this.#apply(G.setCount(this.#design, v, this.#rules, 'compact'), 'preset');
    this.#endCountRun();
  }

  /** @param {string} colour */
  setColour(colour) {
    if (colour !== 'white' && colour !== 'black') return;
    this.#state.colour = colour;
    this.#syncColour();
    this.#render('colour');
  }

  /** @param {string} id */
  selectLayout(id) {
    this.#focusOption = String(id);
    this.#apply(G.applyLayout(this.#design, String(id)), 'option_select');
    this.#focusOption = null;
  }

  /** Arrow keys move between layout choices, as in any radio group. @param {KeyboardEvent} event */
  onOptionKey(event) {
    const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
    if (!step) return;
    event.preventDefault();
    const btns = [...this.refs.options.querySelectorAll('.hxc__opt')];
    const i = btns.indexOf(/** @type {HTMLElement} */ (event.target).closest('.hxc__opt'));
    const next = /** @type {HTMLElement} */ (btns[(i + step + btns.length) % btns.length]);
    if (next) { this.selectLayout(next.dataset.id || ''); this.refs.options.querySelector(`[data-id="${next.dataset.id}"]`)?.focus(); }
  }

  undo() { this.#apply(G.undoDesign(this.#design), 'undo'); }

  redo() { this.#apply(G.redoDesign(this.#design), 'redo'); }

  resetLayout() { this.#apply(G.resetDesign(this.#design), 'reset'); }

  removeSelected() {
    const k = this.#state.selected;
    if (k) this.#edit(k, false);
  }

  /** @param {Event} event */
  onSuspensionToggle(event) {
    this.#state.suspension = /** @type {HTMLInputElement} */ (event.target).checked;
    this.#render('suspension');
  }

  onRoomInput() { this.#renderFit(); }

  clearRoom() {
    if (this.refs.roomW) this.refs.roomW.value = '';
    if (this.refs.roomD) this.refs.roomD.value = '';
    this.#renderFit();
    this.refs.roomW?.focus();
  }

  /** Keyboard operation of the drawing. @param {KeyboardEvent} event */
  onCellKey(event) {
    const el = /** @type {HTMLElement} */ (event.target).closest('[data-cell]');
    const k = el && el.getAttribute('data-cell');
    if (!k) return;
    const ghost = el.getAttribute('data-kind') === 'ghost';
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.#focusCell = k;
      if (ghost) this.#edit(k, true); else this.#tapCell(k);
    } else if (!ghost && (event.key === 'Delete' || event.key === 'Backspace')) {
      event.preventDefault();
      this.#edit(k, false);
    } else if (event.key === 'Escape' && this.#state.selected && !this.refs.dialog.open) {
      this.#focusCell = k;
      this.#state.selected = null;
      this.#drawSvg();
      this.#updateEditControls();
    }
  }

  openEditor() {
    const dlg = /** @type {HTMLDialogElement} */ (this.refs.dialog);
    if (dlg.open) return;
    this.refs.dialogBody.append(this.refs.editor);
    this.classList.add('hxc--editing');
    document.documentElement.style.overflow = 'hidden';
    dlg.showModal();
    this.#drawSvg();
    this.#updateBar();
    this.#track('editor_open');
  }

  closeEditor() { /** @type {HTMLDialogElement} */ (this.refs.dialog).close(); }

  #onEditorClose() {
    this.refs.editorHome.append(this.refs.editor);
    this.classList.remove('hxc--editing');
    document.documentElement.style.overflow = '';
    this.#drawSvg();
    this.#updateBar();
    this.refs.enlargeBtn.focus();
  }

  /** Sticky bar action: move to the review. Never adds anything to the basket. */
  goToReview() {
    const r = this.refs.review;
    const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    r.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'start' });
    r.focus({ preventScroll: true });
    this.#track('review_jump');
  }

  async share() {
    const url = this.#shareUrl();
    try {
      await navigator.clipboard.writeText(url);
      this.#flash('Link copied.', this.refs.shareFlash);
    } catch (e) {
      this.#flash('Copy this page address to share your design.', this.refs.shareFlash);
    }
    this.#emit('share');
  }

  print() {
    this.#pushUrl();
    this.#emit('print');
    window.print();
  }

  /** The printed plan needs the full parts list, which sits in a collapsed disclosure. */
  #beforePrint = () => {
    this.#pushUrl();
    const d = /** @type {HTMLDetailsElement|null} */ (this.querySelector('.hxc__parts'));
    if (!d) return;
    this.#partsWasOpen = d.open;
    d.open = true;
  };

  #afterPrint = () => {
    const d = /** @type {HTMLDetailsElement|null} */ (this.querySelector('.hxc__parts'));
    if (d && this.#partsWasOpen === false) d.open = false;
    this.#partsWasOpen = null;
  };

  async addToBasket() {
    if (this.#busy) return;
    this.#basketMsg('');
    // Guard (fail closed): re-check availability and stock from page-load data.
    const short = this.#shortPart();
    if (!this.#last.ok || short) {
      this.#basketMsg('We do not have enough ' + (short || 'of one part') + ' in stock right now. Please call us and we will help.', true);
      this.refs.addButton.disabled = true;
      this.#track('add_to_cart_failed', { reason: 'stock' });
      return;
    }
    const payload = this.#cartPayload();
    this.#setBusy(true);
    try {
      let result = await this.#postAdd(payload.items);
      if (result.nestedRejected) {
        // nestedRejected fires on ANY 422 for a nested payload (parent_id present),
        // not only when the error text matches the "parent" heuristic above — an
        // unrecognised error message on a store that rejects nesting must not skip
        // this fallback. Retry once, flat, with the Design summary on every line;
        // if that retry also fails, its error surfaces below and we do not retry again.
        const flat = payload.items.map((it) => { const { parent_id, ...rest } = it; return { ...rest, properties: { ...rest.properties, Design: payload.summary } }; });
        result = await this.#postAdd(flat);
      }
      if (!result.ok) throw new Error(result.message || 'We could not add your design to the basket. Please try again.');
      const shortfall = this.#shortfall(payload.items, result.cart.items);
      if (shortfall) {
        await this.#rollback(payload.design_id, result.cart.items);
        throw new Error("We don't have enough of one part in stock right now: " + shortfall + '. Nothing has been added to your basket.');
      }
      this.#track('add_to_cart', { design_id: payload.design_id });
      // The theme setting cart_type decides whether the header renders a
      // <cart-drawer-component> (drawer) or a plain link to the basket page.
      // With a drawer, CartAddEvent lets cart-drawer.js morph and open it from
      // this response. Without one there is nothing to open, so go to the basket
      // page, which is what the stair lights configurator does.
      const drawer = document.querySelector('cart-drawer-component');
      if (drawer) {
        // Same sequence as the theme's other custom add-to-basket paths
        // (snippets/quick-add-custom.liquid): dispatch cart:update with the
        // rendered sections inside detail.data so cart-items-component morphs
        // the drawer from this response, then open the drawer directly. The
        // drawer only opens on its own when the "auto open cart drawer" theme
        // setting is on, and it is off on this store. No setTimeout around
        // open(): cart-drawer.js warns that reintroduces the half-open race.
        this.dispatchEvent(new CartAddEvent({}, this.id || 'hex-configurator', {
          source: 'hex-configurator',
          itemCount: payload.items.reduce((n, it) => n + it.quantity, 0),
          productId: String(this.#data.parent.handle || ''),
          sections: result.cart.sections,
        }));
        // When the theme's "auto open cart drawer" setting is on, the drawer
        // carries `auto-open` and opens itself on the event above; calling
        // open() again would queue a second showModal() and throw.
        if (!drawer.hasAttribute('auto-open') && typeof drawer.open === 'function') drawer.open();
        this.#basketMsg('Your kit has been added to your basket.');
      } else {
        this.#basketMsg('Your kit has been added to your basket. Taking you there now.');
        window.location.assign((window.Theme && window.Theme.routes && window.Theme.routes.cart_url) || '/cart');
      }
    } catch (err) {
      this.#basketMsg(err instanceof Error ? err.message : 'We could not add your design to the basket. Please try again.', true);
      this.#track('add_to_cart_failed', { reason: 'request' });
    } finally {
      this.#setBusy(false);
    }
  }

  #setBusy(on) {
    this.#busy = on;
    const b = this.refs.addButton;
    b.disabled = on || !this.#last.ok;
    b.setAttribute('aria-busy', on ? 'true' : 'false');
    if (on) { b.dataset.label = b.textContent; b.textContent = 'Adding to basket…'; }
    else if (b.dataset.label) { b.textContent = b.dataset.label; }
  }

  /** Name of the first part that is unavailable or short for the current lines, else null. */
  #shortPart() {
    for (const l of this.#last.lines) {
      const p = l.part;
      if (!p.available) return p.title;
      if (p.inventory !== null && p.inventory !== undefined && p.inventory < l.qty) return p.title;
    }
    return null;
  }

  /** Section ids of cart-items components so /cart/add.js returns rendered HTML for the drawer. */
  #cartSectionIds() {
    const ids = [];
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) ids.push(el.dataset.sectionId);
    });
    return ids.join(',');
  }

  /**
   * POST /cart/add.js. Returns { ok, cart, message, nestedRejected, looksLikeNestedRejectionText }.
   * @param {Array<object>} items
   */
  async #postAdd(items) {
    const body = { items };
    const sections = this.#cartSectionIds();
    if (sections) body.sections = sections;
    let res;
    try {
      res = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
    } catch (e) {
      return { ok: false, message: 'We could not reach the basket. Check your connection and try again.' };
    }
    let data = null;
    try { data = await res.json(); } catch (e) { data = null; }
    if (res.ok && data && !data.status) return { ok: true, cart: data };
    const text = ((data && (data.description || data.message)) || '').toString();
    // Heuristic: assumes a store that rejects nested lines (parent_id) mentions
    // "parent" in the error text. This is not a documented Shopify API contract,
    // so it is not trusted on its own — any 422 on a nested payload falls back to
    // flat lines below, whether or not the text matches this pattern.
    const looksLikeNestedRejectionText = /parent/i.test(text);
    const nestedRejected = res.status === 422 && items.some((it) => it.parent_id);
    return { ok: false, nestedRejected, looksLikeNestedRejectionText, message: text || 'We could not add your design to the basket. Please try again.' };
  }

  /** Compare requested with returned quantities by variant id. Returns the short part title or null. */
  #shortfall(requested, returned) {
    const got = new Map();
    for (const line of returned || []) {
      const vid = Number(line.variant_id || line.id);
      got.set(vid, (got.get(vid) || 0) + Number(line.quantity || 0));
    }
    for (const it of requested) {
      if ((got.get(Number(it.id)) || 0) < it.quantity) {
        const line = this.#last.lines.find((l) => l.part.id === it.id);
        return line ? line.part.title : 'one part';
      }
    }
    return null;
  }

  /** Remove every returned line carrying this design id in one /cart/update.js call. */
  async #rollback(designId, returned) {
    const updates = {};
    for (const line of returned || []) {
      if (line.key && line.properties && line.properties._design_id === designId) updates[line.key] = 0;
    }
    if (!Object.keys(updates).length) return;
    const body = { updates };
    const sections = this.#cartSectionIds();
    if (sections) body.sections = sections;
    let cart = null;
    try {
      const res = await fetch(window.Theme?.routes?.cart_update_url || '/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      cart = await res.json();
    } catch (e) {
      console.error('[hex-configurator] rollback failed', e);
    }
    // cart-drawer.js#handleCartAdd opens on ANY event named ThemeEvents.cartUpdate
    // ('cart:update') as long as the header has auto-open set — it only checks
    // hasAttribute('auto-open'), never event.detail, and CartAddEvent/CartUpdateEvent
    // both dispatch under that exact same name, so neither class can signal "update
    // but don't open". CartErrorEvent uses a distinct 'cart:error' name the drawer
    // never listens for, so it refreshes nothing and opens nothing on its own; the
    // header bubble is corrected separately, straight from the rollback response.
    this.dispatchEvent(new CartErrorEvent(
      this.id || 'hex-configurator',
      'Design removed from basket after a stock shortfall.',
      '',
      null
    ));
    this.#refreshCartBubble(cart);
  }

  /**
   * Sets the header cart bubble to the real post-rollback count, the same way
   * cart-icon.js persists and restores it on pageshow (sessionStorage 'cart-count'),
   * then calls its own public renderCartBubble so the visible count is corrected
   * immediately without dispatching a cart:update event that would reopen the drawer.
   * @param {{item_count?: number}|null} cart
   */
  #refreshCartBubble(cart) {
    if (!cart || typeof cart.item_count !== 'number') return;
    try {
      sessionStorage.setItem('cart-count', JSON.stringify({ value: String(cart.item_count), timestamp: Date.now() }));
    } catch (e) { /* storage full or blocked — non-critical */ }
    const cartIcon = document.querySelector('cart-icon');
    if (cartIcon && typeof cartIcon.renderCartBubble === 'function') {
      cartIcon.renderCartBubble(cart.item_count, false, false);
    }
  }

  // ---------- rendering ----------

  /** Redraw every view from #design. Only the URL and analytics are deferred. */
  #render(action) {
    if (!this.#ready) return;
    const d = G.derive(this.#design.cells, this.#rules);
    Object.assign(this.#last, { d, g: d.g, power: d.power, bom: d.parts });
    this.#syncCount();
    this.#renderOptions();
    this.#drawSvg();
    this.#renderReview();
    this.#renderFit();
    this.#updateEditControls();
    clearTimeout(this.#timer);
    this.#timer = window.setTimeout(() => { this.#pushUrl(); this.#emit(action); }, TRACK_MS);
  }

  #syncCount() {
    const n = this.#design.cells.size;
    this.refs.nRange.value = String(n);
    this.refs.nNum.value = String(n);
    if (this.refs.nDown) this.refs.nDown.disabled = n <= 1;
    if (this.refs.nUp) this.refs.nUp.disabled = n >= this.#maxN();
    this.refs.preset.value = this.#data.kits && this.#data.kits[n] ? String(n) : '';
    if (this.refs.quoteNote) this.refs.quoteNote.hidden = n < this.#maxN();
  }

  #syncColour() {
    for (const b of this.refs.colourButtons || []) b.setAttribute('aria-pressed', b.dataset.colour === this.#state.colour ? 'true' : 'false');
  }

  #renderOptions() {
    const wrap = this.refs.options;
    const { options, selected } = G.layoutChoices(this.#design);
    const n = this.#design.cells.size;
    wrap.replaceChildren(...options.map((o, i) => {
      const b = document.createElement('button');
      const on = o.id === selected;
      b.type = 'button';
      b.className = 'hxc__opt';
      b.dataset.id = o.id;
      b.setAttribute('role', 'radio');
      b.setAttribute('aria-checked', on ? 'true' : 'false');
      b.tabIndex = on || (!selected && i === 0) ? 0 : -1;
      b.setAttribute('on:click', '/selectLayout/' + o.id);
      b.setAttribute('on:keydown', '/onOptionKey');
      const g = G.analyse(o.cells, this.#rules);
      const t = document.createElement('span');
      t.className = 'hxc__opt-title';
      t.textContent = o.title;
      const s = document.createElement('span');
      s.className = 'hxc__opt-sub';
      s.textContent = g.tubes + ' tubes, ' + m2(g.width + this.#rules.body_allowance_mm) + ' × ' + m2(g.depth + this.#rules.body_allowance_mm) + ' m';
      b.title = o.subtitle;
      b.append(this.#drawMini(g), t, s);
      return b;
    }));
    const note = this.refs.layoutNote;
    note.classList.toggle('hxc__custom', !selected);
    note.textContent = selected
      ? 'Layouts for ' + plural(n, 'hexagon') + '.'
      : 'Custom design with ' + plural(n, 'hexagon') + '. Choosing a layout replaces it, and Undo brings it back.';
    if (this.#focusOption) /** @type {HTMLElement|null} */ (wrap.querySelector(`[data-id="${this.#focusOption}"]`))?.focus();
  }

  #drawMini(g) {
    const pad = 60;
    const svg = svgEl('svg', { viewBox: `${g.bbox.minX - pad} ${g.bbox.minY - pad} ${g.width + 2 * pad} ${g.depth + 2 * pad}`, 'aria-hidden': 'true', class: 'hxc__opt-svg' });
    for (const e of g.edges.values()) svg.appendChild(svgEl('line', { x1: e.a[0], y1: e.a[1], x2: e.b[0], y2: e.b[1], stroke: 'currentColor', 'stroke-width': 60, 'stroke-linecap': 'round' }));
    return svg;
  }

  #updateEditControls() {
    const s = this.#design;
    const sel = this.#state.selected;
    this.refs.undoBtn.disabled = !s.undo.length;
    this.refs.redoBtn.disabled = !s.redo.length;
    const canRemove = !!sel && this.#removable(sel);
    this.refs.removeBtn.disabled = !canRemove;
    const t = G.resetTarget(s);
    const reset = this.refs.resetBtn;
    reset.hidden = !t || G.sameShape(t.cells, s.cells);
    if (t) reset.textContent = 'Back to ' + t.title + ', ' + plural(t.n, 'hexagon');
    this.refs.editHint.textContent = !sel
      ? 'Tap + to add a hexagon. Tap a hexagon to select it, then remove it. Drag a hexagon to move it.'
      : canRemove
        ? 'Hexagon selected. Tap it again or press Remove to take it out, or drag it to move it.'
        : 'This hexagon holds the light together, so it cannot be removed. You can drag it to move it.';
  }

  /** The editable drawing: add spaces, hexagons, tubes, joints, plugs and dimensions, in mm. */
  #drawSvg() {
    const rules = this.#rules;
    const cells = this.#design.cells;
    const { g, power } = this.#last;
    if (!g) return;
    const n = cells.size;
    const sel = this.#state.selected;
    const ghosts = n < this.#maxN() ? G.frontier(cells) : new Set();
    let { minX, maxX, minY, maxY } = g.bbox;
    for (const k of ghosts) {
      for (const p of G.corners(...G.parse(k), rules)) {
        if (p[0] < minX) minX = p[0]; if (p[0] > maxX) maxX = p[0];
        if (p[1] < minY) minY = p[1]; if (p[1] > maxY) maxY = p[1];
      }
    }
    // Estimate the mm-per-pixel scale so labels have room; #fitLabels corrects it once drawn.
    const k0 = (maxX - minX + 400) / Math.max(this.refs.canvas.clientWidth || 600, 200);
    const font = Math.max(90, LABEL_PX * k0);
    // Labels sit above and to the left, so only those sides need room for them.
    const pad = 80 + font * 1.6, edge = 100;
    const vb = [minX - pad, minY - pad, maxX - minX + pad + edge, maxY - minY + pad + edge];
    const dims = metres(g.width + rules.body_allowance_mm) + ' by ' + metres(g.depth + rules.body_allowance_mm);
    const svg = svgEl('svg', { viewBox: vb.join(' '), role: 'group', class: 'hxc__svg hxc__svg--' + this.#state.colour, 'aria-label': `Design editor: ${plural(n, 'hexagon')}, ${g.tubes} tubes, ${dims}`, 'aria-describedby': 'hxc-edit-hint' });

    for (const k of ghosts) {
      const [q, r] = G.parse(k);
      const [cx, cy] = G.centre(q, r, rules);
      const grp = svgEl('g', { class: 'hxc__ghost', 'data-cell': k, 'data-kind': 'ghost', tabindex: 0, role: 'button', 'aria-label': 'Add a hexagon here', 'on:keydown': '/onCellKey' });
      grp.append(svgEl('polygon', { points: poly(G.corners(q, r, rules)) }), svgEl('circle', { class: 'hxc__badge', cx, cy, r: 120 }));
      const t = svgEl('text', { x: cx, y: cy, 'font-size': 200, 'text-anchor': 'middle', dy: 70 });
      t.textContent = '+';
      grp.appendChild(t);
      svg.appendChild(grp);
    }
    let i = 0;
    for (const k of cells) {
      i++;
      const [q, r] = G.parse(k);
      const [cx, cy] = G.centre(q, r, rules);
      const isSel = k === sel;
      const removable = this.#removable(k);
      const label = 'Hexagon ' + i + ' of ' + n + (isSel ? ', selected. ' + (removable ? 'Press Enter or Delete to remove it.' : 'It holds the light together, so it cannot be removed.') : '. Press Enter to select it.');
      const grp = svgEl('g', { class: 'hxc__cell' + (isSel ? ' hxc__cell--selected' : '') + (removable ? '' : ' hxc__cell--locked'), 'data-cell': k, 'data-kind': 'cell', tabindex: 0, role: 'button', 'aria-pressed': isSel ? 'true' : 'false', 'aria-label': label, 'on:keydown': '/onCellKey' });
      grp.appendChild(svgEl('polygon', { points: poly(G.corners(q, r, rules)) }));
      if (isSel && removable) {
        grp.appendChild(svgEl('circle', { class: 'hxc__badge hxc__badge--remove', cx, cy, r: 130 }));
        const x = svgEl('text', { class: 'hxc__x', x: cx, y: cy, 'font-size': 220, 'text-anchor': 'middle', dy: 76 });
        x.textContent = '×';
        grp.appendChild(x);
      }
      svg.appendChild(grp);
    }

    // Everything below is picture only: hidden from assistive technology and from the pointer.
    const art = svgEl('g', { 'aria-hidden': 'true', 'pointer-events': 'none' });
    for (const e of g.edges.values()) {
      art.appendChild(svgEl('line', { class: 'hxc__tube-edge', x1: e.a[0], y1: e.a[1], x2: e.b[0], y2: e.b[1], 'stroke-width': 48, 'stroke-linecap': 'round' }));
      art.appendChild(svgEl('line', { class: 'hxc__tube', x1: e.a[0], y1: e.a[1], x2: e.b[0], y2: e.b[1], 'stroke-width': 30, 'stroke-linecap': 'round' }));
    }
    // Connectors are drawn by shape only. The builder places power connectors to
    // count the parts, but the customer can connect power anywhere suitable, so the
    // screen shows no plug positions. The printed plan shows them as numbered
    // suggestions (see .hxc__print-mark and the printPower caption).
    for (const v of g.verts.values()) {
      art.appendChild(svgEl('circle', { class: 'hxc__joint hxc__joint--' + (v.edges.size === 3 ? 'y' : 'v'), cx: v.p[0], cy: v.p[1], r: 34, 'stroke-width': 8 }));
    }
    const firstVert = g.verts.values().next().value;
    const plugPts = n === 1 ? (firstVert ? [firstVert] : []) : power.points;
    const mx = (g.bbox.minX + g.bbox.maxX) / 2, my = (g.bbox.minY + g.bbox.maxY) / 2;
    const marks = svgEl('g', { class: 'hxc__print-mark' });
    plugPts.forEach((v, j) => {
      const dx = v.p[0] - mx, dy = v.p[1] - my;
      const L = Math.hypot(dx, dy) || 1;
      const ex = v.p[0] + (dx / L) * 220, ey = v.p[1] + (dy / L) * 220;
      marks.appendChild(svgEl('line', { class: 'hxc__plug', x1: v.p[0], y1: v.p[1], x2: ex, y2: ey, 'stroke-width': 16, 'stroke-linecap': 'round' }));
      marks.appendChild(svgEl('circle', { class: 'hxc__plug-dot', cx: v.p[0], cy: v.p[1], r: 52, 'stroke-width': 8 }));
      const t = svgEl('text', { class: 'hxc__svg-label hxc__plug-label', x: ex, y: ey - font * 0.4, 'font-size': font, 'text-anchor': 'middle' });
      t.textContent = String(j + 1);
      marks.appendChild(t);
    });
    art.appendChild(marks);
    // Dimension lines sit just outside the add spaces so the two never overlap.
    const allow = rules.body_allowance_mm;
    const y0 = minY - font * 0.5, x0 = minX - font * 0.5;
    art.append(
      svgEl('line', { class: 'hxc__dim', x1: g.bbox.minX, y1: y0, x2: g.bbox.maxX, y2: y0 }),
      svgEl('line', { class: 'hxc__dim', x1: x0, y1: g.bbox.minY, x2: x0, y2: g.bbox.maxY }),
    );
    const tw = svgEl('text', { class: 'hxc__svg-label hxc__dim-label', x: mx, y: y0 - font * 0.35, 'font-size': font, 'text-anchor': 'middle' });
    tw.textContent = metres(g.width + allow);
    const td = svgEl('text', { class: 'hxc__svg-label hxc__dim-label', x: x0 - font * 0.35, y: my, 'font-size': font, 'text-anchor': 'middle', transform: `rotate(-90 ${x0 - font * 0.35} ${my})` });
    td.textContent = metres(g.depth + allow);
    art.append(tw, td);
    svg.appendChild(art);

    const canvas = this.refs.canvas;
    const hadFocus = canvas.contains(document.activeElement) ? document.activeElement?.getAttribute('data-cell') : null;
    canvas.replaceChildren(svg);
    this.#attachPointer(svg);
    this.#fitLabels();
    const focusKey = this.#focusCell || hadFocus;
    this.#focusCell = null;
    if (focusKey) {
      const target = /** @type {SVGElement|null} */ (svg.querySelector(`[data-cell="${focusKey}"]`) || svg.querySelector('.hxc__cell'));
      target?.focus();
    }
  }

  /** Keep drawing labels and dimension strokes at a readable on-screen size. */
  #fitLabels() {
    const svg = /** @type {SVGSVGElement|null} */ (this.refs.canvas.querySelector('.hxc__svg'));
    if (!svg) return;
    const box = svg.getBoundingClientRect();
    const vb = svg.viewBox.baseVal;
    if (!box.width || !box.height || !vb) return;
    const k = Math.max(vb.width / box.width, vb.height / box.height);
    const font = String(Math.max(90, LABEL_PX * k));
    svg.querySelectorAll('.hxc__svg-label').forEach((t) => t.setAttribute('font-size', font));
    // Tubes and joints never thinner on screen than these pixel sizes, so a white frame keeps a visible outline.
    const set = (sel, attr, px, minMm) => svg.querySelectorAll(sel).forEach((el) => el.setAttribute(attr, String(Math.max(minMm, px * k))));
    set('.hxc__dim', 'stroke-width', 1.5, 8);
    set('.hxc__tube-edge', 'stroke-width', 6, 48);
    set('.hxc__tube', 'stroke-width', 3.4, 30);
    set('.hxc__joint--v, .hxc__joint--y', 'r', 3.5, 34);
    set('.hxc__plug-dot', 'r', 5, 52);
  }

  /** Summary, power, suspension, price, comparison, parts table and stock state. */
  #renderReview() {
    const rules = this.#rules;
    const { d, g, power } = this.#last;
    const bom = d.parts;
    const n = d.n;
    const dims = metres(d.widthMm) + ' × ' + metres(d.depthMm);

    // stats beside the drawing
    const stats = [[n, n === 1 ? 'hexagon' : 'hexagons'], [g.tubes, 'tubes'], [power.totalW + 'W', 'total'], [(g.tubes * rules.lumens_per_tube).toLocaleString('en-GB'), 'lumens'], [dims, 'approx. footprint']];
    this.refs.stats.replaceChildren(...stats.map(([v, l]) => {
      const span = document.createElement('span');
      const b = document.createElement('b');
      b.textContent = String(v);
      span.append(b, ' ' + l);
      return span;
    }));

    // parts lines and price
    const suspPart = this.#parts.suspension;
    const codes = this.#state.suspension && suspPart ? [...LINE_ORDER, 'suspension'] : LINE_ORDER;
    const lines = [];
    let total = 0;
    let short = null;
    for (const code of codes) {
      const qty = bom[code];
      const part = this.#part(code);
      if (!qty) continue;
      if (!part) { short = short || LINE_LABELS[code]; continue; }
      const line = { code, qty, part, each: part.price, total: part.price * qty };
      total += line.total;
      lines.push(line);
      if (!short && (!part.available || (part.inventory !== null && part.inventory !== undefined && part.inventory < qty))) short = part.title;
    }
    this.#last.lines = lines;
    this.#last.total = total;
    this.#last.ok = !short && lines.length > 0;

    this.refs.bomBody.replaceChildren(...lines.map((l) => {
      const tr = document.createElement('tr');
      const name = document.createElement('td');
      name.textContent = l.part.title;
      const sku = document.createElement('span');
      sku.className = 'hxc__sku';
      sku.textContent = l.part.sku || '';
      name.append(document.createElement('br'), sku);
      const cells = [name];
      for (const v of [String(l.qty), this.#money(l.each), this.#money(l.total)]) {
        const td = document.createElement('td');
        td.className = 'hxc__n';
        td.textContent = v;
        cells.push(td);
      }
      tr.append(...cells);
      return tr;
    }));
    const price = this.#money(total);
    const vat = this.#vatLabel();
    this.refs.price.textContent = price;
    this.refs.barPrice.textContent = price;
    this.refs.dialogStats.textContent = plural(n, 'hexagon') + ', ' + dims + ', ' + price + ' ' + vat;
    for (const el of this.refs.vatLabels || []) el.textContent = vat;
    // Ex VAT view: the basket and checkout still charge VAT, so say what that comes to.
    if (this.refs.vatAlt) {
      this.refs.vatAlt.hidden = !this.#exVat();
      this.refs.vatAlt.textContent = this.#exVat() ? this.#money(total, true) + ' inc VAT' : '';
    }

    // customer summary
    const perFeed = Math.round(power.wattsPerFeed);
    const rows = [
      ['Hexagons', String(n)],
      ['Design footprint (approx.)', dims],
      ['Frame colour', this.#state.colour === 'black' ? 'Black' : 'White'],
      ['Plugs needed', power.feeds === 1 ? '1 UK plug, ' + perFeed + 'W' : power.feeds + ' UK plugs, about ' + perFeed + 'W each'],
    ];
    const chainsPerSet = rules.chains_per_suspension_set;
    const sets = plural(bom.suspension, 'set') + ' of ' + chainsPerSet + ' chains';
    if (suspPart && this.refs.suspensionToggle) rows.push(['Mounting', this.#state.suspension ? 'Hanging on ' + plural(bom.chains, 'chain') + ', ' + sets : 'Fixed flat, with the fixings included']);
    this.refs.summary.replaceChildren(...rows.flatMap(([k, v]) => {
      const dt = document.createElement('dt');
      dt.textContent = k;
      const dd = document.createElement('dd');
      dd.textContent = v;
      return [dt, dd];
    }));

    // suspension option, with its price impact
    if (this.refs.suspensionNote && suspPart) {
      this.refs.suspensionNote.textContent = 'Hangs the light below the ceiling on chains: ' + plural(bom.chains, 'chain') + ' recommended, one per hexagon and never fewer than two. Leave unticked to fix it flat to a ceiling or wall with the fixings included. Adds ' + sets + ' for ' + plural(n, 'hexagon') + ', ' + this.#money(suspPart.price * bom.suspension) + '.';
    }

    // power detail
    const pb = this.refs.powerBox;
    pb.className = 'hxc__power' + (power.feeds === 1 ? ' hxc__power--ok' : '');
    pb.replaceChildren();
    const b = document.createElement('b');
    if (power.feeds === 1) {
      b.textContent = '1 plug';
      pb.append(b, ' needed (' + perFeed + 'W). Plugs into a standard 13A socket.');
    } else {
      b.textContent = power.feeds + ' plugs';
      pb.append(b, ' needed, about ' + perFeed + 'W each. Each plug needs its own socket or fused spur.');
    }
    pb.append(' You can choose the power connection position during installation.');
    if (this.refs.printPower) this.refs.printPower.textContent = 'Numbered green dots show suggested connection positions only. This design needs ' + plural(power.feeds, 'plug') + '; you can choose the power connection position during installation.';
    if (power.totalW > rules.warn_over_watts) pb.append(' Systems over ' + rules.warn_over_watts.toLocaleString('en-GB') + 'W may need their own circuit. Ask an electrician.');

    // stock and button
    if (!this.#busy) this.refs.addButton.disabled = !this.#last.ok;
    this.refs.stockNote.textContent = short ? 'We do not have enough ' + short + ' in stock right now. Please call us and we will help.' : '';
  }

  /**
   * Optional room check (spec 4.4 item 7, extended 7 Oct 2026). Recommends a
   * range of hexagons for the ceiling (G.recommendCount), whatever design is
   * on screen, and offers each as a button. Then says whether the current
   * design fits. Guidance only: the design changes only when the customer
   * taps a recommended number (useCount).
   */
  #renderFit() {
    const W = this.refs.roomW, D = this.refs.roomD, msg = this.refs.fitMsg, rec = this.refs.recMsg;
    const d = this.#last.d;
    if (!W || !D || !msg || !rec || !d) return;
    const key = W.value + '|' + D.value;
    if (this.#rec.key !== key) this.#rec = { key, r: G.recommendCount(W.value, D.value, this.#rules) };
    const r = this.#rec.r;
    const f = G.fitCheck(W.value, D.value, d.widthMm, d.depthMm);
    const n = this.#design.cells.size;
    W.setAttribute('aria-invalid', Number.isNaN(G.parseMetres(W.value)) ? 'true' : 'false');
    D.setAttribute('aria-invalid', Number.isNaN(G.parseMetres(D.value)) ? 'true' : 'false');
    if (this.refs.fitClear) this.refs.fitClear.hidden = !W.value && !D.value;
    const room = f.w + ' × ' + f.d + ' m';
    const need = f.needW ? f.needW.toFixed(2) + ' × ' + f.needD.toFixed(2) + ' m' : '';
    const ask = 'Enter both measurements and we will recommend how many hexagons to use.';

    rec.className = 'hxc__fitmsg hxc__fitmsg--' + r.status;
    rec.textContent = {
      empty: ask,
      partial: ask,
      invalid: 'Enter each measurement in metres, above 0, for example 3.5.',
      none: 'None of our hexagon lights fit a ceiling this size. Check your measurements, or call us and we will help.',
      ok: r.status === 'ok' ? this.#recText(r) : '',
    }[r.status];
    if (this.refs.recQuote) this.refs.recQuote.hidden = !r.capped;
    this.#renderRecButtons(r, n);

    const measured = f.status === 'fits' || f.status === 'too_big';
    msg.hidden = !measured || r.status === 'none';
    msg.className = 'hxc__fitmsg hxc__fitmsg--' + f.status;
    msg.textContent = f.status === 'fits'
      ? 'Your current design of ' + plural(n, 'hexagon') + ' (' + metres(d.widthMm) + ' × ' + metres(d.depthMm) + ') fits, with at least 300mm to spare.'
      : f.status === 'too_big'
        ? 'Your current design of ' + plural(n, 'hexagon') + ' is too large for ' + room + '. It needs about ' + need + ', either way round. Tap a recommended number to resize it.'
        : '';

    const warn = f.status === 'too_big' ? 'Too large for the ' + room + ' ceiling you entered. It needs about ' + need + '. Choose one of the recommended numbers in step 1, another layout, or check your measurements.' : '';
    for (const el of [this.refs.stageFit, this.refs.reviewFit]) {
      if (!el) continue;
      el.hidden = !warn;
      el.textContent = warn;
    }
  }

  /** The recommendation sentence for a room that takes at least one design. */
  #recText(r) {
    const range = r.low === r.high ? plural(r.low, 'hexagon') : r.low + ' to ' + r.high + ' hexagons';
    let t = 'We recommend ' + range + ' for this space.';
    if (r.capped) t += ' Up to ' + r.max + ' can be ordered online and your space could take more.';
    else if (r.max > r.high) t += ' Up to ' + r.max + ' will fit if you want more light.';
    else t += ' That is the most that will fit.';
    return t + ' Tap a number to use it.';
  }

  /**
   * One button per recommended number: the low end, a middle count, the high
   * end and the most that fit, each a count that fits. Rebuilt only when the
   * numbers change, so focus stays on the button just tapped.
   */
  #renderRecButtons(r, n) {
    const wrap = this.refs.recBtns;
    if (!wrap) return;
    let nums = [];
    if (r.status === 'ok') {
      const mid = (r.low + r.high) / 2;
      const inRange = [...r.fits.keys()].filter((v) => v >= r.low && v <= r.high);
      const near = inRange.reduce((a, v) => (Math.abs(v - mid) < Math.abs(a - mid) ? v : a), r.low);
      nums = [...new Set([r.low, near, r.high, r.max])].sort((a, b) => a - b);
    }
    wrap.hidden = !nums.length;
    const key = nums.join();
    if (wrap.dataset.key !== key) {
      wrap.dataset.key = key;
      wrap.replaceChildren(...nums.map((v) => {
        const top = v > r.high;
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'hxc__rec' + (top ? ' hxc__rec--max' : '');
        b.dataset.n = String(v);
        b.textContent = top ? v + ' max' : String(v);
        b.setAttribute('aria-label', 'Use ' + plural(v, 'hexagon') + (top ? ', the most that fit' : ''));
        b.setAttribute('on:click', '/useCount/' + v);
        return b;
      }));
    }
    for (const b of wrap.children) b.setAttribute('aria-pressed', Number(b.dataset.n) === n ? 'true' : 'false');
  }

  /**
   * Customer's VAT display choice from the header switch, stored the same way
   * the rest of the theme reads it (layout/theme.liquid, stair lights builder).
   */
  #exVat() {
    if (!this.#taxesIncluded) return false;
    try { return localStorage.getItem('vatPreference') === 'ex'; } catch (e) { return false; }
  }

  #vatLabel() { return this.#exVat() || !this.#taxesIncluded ? 'ex VAT' : 'inc VAT'; }

  /**
   * Format a price for display. Liquid gives prices in pence including VAT;
   * ex VAT is Math.round(pence / 1.2), the same sum the theme uses everywhere.
   * Display only: prices, totals and the basket payload are never changed.
   * @param {number} minor @param {boolean} [asIs] show the stored price whatever the switch says
   */
  #money(minor, asIs = false) {
    const shown = !asIs && this.#exVat() ? Math.round(minor / 1.2) : minor;
    try {
      return formatMoney(shown, this.#data.money_format || '{{amount}}', this.#data.currency || 'GBP');
    } catch (e) {
      return (shown / 100).toFixed(2);
    }
  }

  #onVatChange = () => {
    if (this.#ready && this.#last.d) this.#renderReview();
  };

  #flash(text, el = this.refs.flash) {
    el.textContent = text;
    setTimeout(() => { if (el.textContent === text) el.textContent = ''; }, 3000);
  }

  /** Basket feedback beside the button. @param {string} text @param {boolean} [error] */
  #basketMsg(text, error = false) {
    const m = this.refs.basketMsg;
    m.hidden = !text;
    m.textContent = text;
    m.classList.toggle('hxc__basket-msg--error', error);
    m.setAttribute('role', error ? 'alert' : 'status');
  }

  /** Sticky mobile total and "Review my kit", only while the review is out of view. */
  #updateBar = () => {
    const bar = this.refs.bar;
    if (!bar) return;
    const show = this.#ready && !!this.#mq?.matches && this.#seen.app && !this.#seen.review && !this.#seen.typing && !this.refs.dialog.open;
    bar.hidden = !show;
    // The storefront preview bar (theme previews only) is fixed to the bottom too; sit above it.
    const pb = document.getElementById('PBarNextFrameWrapper');
    bar.style.bottom = show && pb && pb.offsetHeight ? pb.offsetHeight + 'px' : '';
    document.documentElement.style.scrollPaddingBottom = show ? bar.offsetHeight + 16 + 'px' : '';
  };

  // ---------- share URL and analytics ----------

  #shareUrl() {
    const url = new URL(location.href);
    url.search = '?' + G.encodeDesign(this.#design.cells, this.#state.colour);
    url.hash = '';
    return url.toString();
  }

  #pushUrl() {
    const url = this.#shareUrl();
    if (url !== location.href) history.replaceState(null, '', url);
    if (this.refs.printShare) this.refs.printShare.textContent = 'Design link: ' + url;
  }

  #analyticsPayload(action, extra) {
    const { g, power, total } = this.#last;
    return { action, hexagons: this.#design.cells.size, tubes: g ? g.tubes : 0, colour: this.#state.colour, feeds: power ? power.feeds : 0, watts: power ? power.totalW : 0, value: Math.round(total) / 100, ...(extra || {}) };
  }

  /** Publish to Shopify analytics (custom pixels) and to the GTM dataLayer. Never throws. */
  #track(action, extra) {
    const payload = this.#analyticsPayload(action, extra);
    try {
      const sa = /** @type {any} */ (window).Shopify?.analytics;
      if (sa && typeof sa.publish === 'function') sa.publish('hex_configurator', payload);
    } catch (e) { /* analytics must never block the builder */ }
    try {
      const w = /** @type {any} */ (window);
      w.dataLayer = w.dataLayer || [];
      w.dataLayer.push({ event: 'hex_configurator', ...payload });
    } catch (e) { /* ignore */ }
    return payload;
  }

  #emit(action) {
    if (!this.#last.g || !this.#last.power) return;
    const detail = this.#track(action);
    this.dispatchEvent(new CustomEvent('hex:change', { bubbles: true, detail }));
  }

  // ---------- cart payload (spec 4.6) ----------

  #cartPayload() {
    const { g, power, lines } = this.#last;
    const s = this.#state;
    const n = this.#design.cells.size;
    const allow = this.#rules.body_allowance_mm;
    const id = 'HX-' + Math.random().toString(36).slice(2, 7).toUpperCase();
    const summary = `${n} hexagons, ${s.colour} frame, ${power.feeds} plug${power.feeds > 1 ? 's' : ''}, ${metres(g.width + allow)} × ${metres(g.depth + allow)}`;
    const parent = this.#data.parent[s.colour];
    const shareUrl = this.#shareUrl();
    const shared = { _design_id: id, _bundle_id: id, _bundle_label: 'Custom hexagon light: ' + summary, _bundle_parent_handle: this.#data.parent.handle };
    const items = [{ id: parent.id, quantity: 1, properties: { ...shared, _design_url: shareUrl, _hexagons: String(n), Design: summary } }];
    for (const l of lines) items.push({ id: l.part.id, quantity: l.qty, parent_id: parent.id, properties: { ...shared } });
    return { design_id: id, summary, items };
  }

  // ---------- pointer editor (spec 4.5) ----------

  /**
   * Tap a + to add; tap a hexagon to select it, tap again to remove; press and
   * drag a hexagon to move it. On the page the drawing lets vertical swipes
   * scroll (touch-action: pan-y), and a swipe that turns into a scroll arrives
   * as pointercancel, which is ignored, so scrolling never edits the design.
   * In the enlarged editor drags work in every direction.
   */
  #attachPointer(svg) {
    /** @type {any} */
    let down = null;
    const canvas = this.refs.canvas;
    const rules = this.#rules;
    const svgPoint = (e) => {
      const pt = svg.createSVGPoint();
      pt.x = e.clientX;
      pt.y = e.clientY;
      const ctm = svg.getScreenCTM();
      return ctm ? pt.matrixTransform(ctm.inverse()) : pt;
    };
    const lift = () => {
      down.lifted = true;
      canvas.classList.add('hxc__canvas--dragging');
      down.targets = G.moveTargets(this.#design.cells, down.k);
      svg.querySelectorAll('g.hxc__ghost').forEach((gh) => {
        gh.classList.add(down.targets.has(gh.getAttribute('data-cell')) ? 'hxc__ghost--target' : 'hxc__ghost--dim');
      });
      down.el.classList.add('hxc__cell--dragging');
      const [q, r] = G.parse(down.k);
      const lp = svgEl('polygon', { class: 'hxc__lift hxc__lift--ok', points: poly(G.corners(q, r, rules)), 'stroke-width': 30, 'stroke-linejoin': 'round' });
      svg.appendChild(lp);
      down.liftEl = lp;
      down.snapped = down.k;
    };
    svg.addEventListener('pointerdown', (e) => {
      const target = /** @type {Element} */ (e.target).closest('g.hxc__cell, g.hxc__ghost');
      if (!target || e.button) return;
      const type = target.classList.contains('hxc__ghost') ? 'ghost' : 'cell';
      down = { k: target.getAttribute('data-cell'), type, x: e.clientX, y: e.clientY, moved: false, lifted: false, el: target, targets: null, liftEl: null, snapped: null };
      if (type === 'cell') {
        try { svg.setPointerCapture(e.pointerId); } catch (err) { /* ignore */ }
      }
    });
    svg.addEventListener('pointermove', (e) => {
      if (!down) return;
      if (!down.lifted) {
        if (Math.hypot(e.clientX - down.x, e.clientY - down.y) < DRAG_PX) return;
        down.moved = true;
        if (down.type !== 'cell' || this.#design.cells.size < 2) return;
        lift();
      }
      const p = svgPoint(e);
      const cellK = G.pixelToCell(p.x, p.y, rules);
      const snap = down.targets.has(cellK) ? cellK : null;
      if (snap !== down.snapped) {
        down.snapped = snap;
        const [q, r] = G.parse(snap || down.k);
        down.liftEl.setAttribute('points', poly(G.corners(q, r, rules)));
        down.liftEl.classList.toggle('hxc__lift--ok', !!snap);
        down.liftEl.classList.toggle('hxc__lift--bad', !snap);
      }
    });
    const end = (cancelled) => {
      if (!down) return;
      const d = down;
      down = null;
      canvas.classList.remove('hxc__canvas--dragging');
      if (!d.lifted) {
        if (cancelled || d.moved) return;
        if (d.type === 'ghost') this.#edit(d.k, true);
        else this.#tapCell(d.k);
        return;
      }
      const dest = d.snapped;
      if (!cancelled && dest && dest !== d.k) {
        this.#move(d.k, dest);
      } else {
        this.#drawSvg();
        if (!cancelled && !dest) this.#flash('Hexagons must touch the rest of the light.');
      }
    };
    svg.addEventListener('pointerup', () => end(false));
    svg.addEventListener('pointercancel', () => end(true));
  }
}

if (!customElements.get('hex-configurator')) {
  customElements.define('hex-configurator', HexConfigurator);
}
